import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Capacitor } from '@capacitor/core';
import { logger } from '../utils/logger';

/**
 * Analytics Service - Centraliza tracking de eventos Google Analytics
 * 
 * Usa Firebase Analytics plugin para Capacitor
 * Disponível apenas em apps nativos (iOS/Android)
 * 
 * Eventos customizados:
 * - Logins (psicologo, paciente, admin)
 * - Cadastros
 * - Alertas de risco
 * - Criação de diários
 * - Agendamentos, relatórios, suporte
 */
export class AnalyticsService {
    private static instance: AnalyticsService;
    private isEnabled = false;

    private constructor() { }

    static getInstance(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }
        return AnalyticsService.instance;
    }

    /**
     * Inicializar Analytics
     * Deve ser chamado no bootstrap da aplicação
     */
    async initialize() {
        // Analytics só funciona em apps nativos (não em web)
        if (!Capacitor.isNativePlatform()) {
            logger.log('[Analytics] Disponível apenas em apps nativos');
            return;
        }

        try {
            await FirebaseAnalytics.setCollectionEnabled({ enabled: true });
            this.isEnabled = true;
            logger.log('[Analytics] Inicializado com sucesso');
        } catch (error) {
            logger.error('[Analytics] Erro ao inicializar:', error);
        }
    }

    /**
     * Rastrear visualização de tela
     */
    async logScreen(screenName: string, screenClass?: string) {
        if (!this.isEnabled) return;

        try {
            await FirebaseAnalytics.setScreenName({
                screenName,
                nameOverride: screenClass || screenName,
            });
            logger.log(`[Analytics] Tela: ${screenName}`);
        } catch (error) {
            logger.error('[Analytics] Erro ao logar tela:', error);
        }
    }

    /**
     * Eventos Customizados - Autenticação
     */
    async logPsychologistLogin(userId: string) {
        await this.logEvent('psych_login', {
            user_type: 'psychologist',
            user_id: userId,
        });
    }

    async logPatientLogin(userId: string) {
        await this.logEvent('patient_login', {
            user_type: 'patient',
            user_id: userId,
        });
    }

    async logAdminLogin(userId: string, role: string) {
        await this.logEvent('admin_login', {
            user_type: 'admin',
            admin_role: role,
            user_id: userId,
        });
    }

    async logUserRegistration(userType: 'psychologist' | 'patient', userId: string) {
        await this.logEvent('sign_up', {
            method: userType,
            user_id: userId,
        });
    }

    /**
     * Eventos Customizados - Ações Principais
     */
    async logPatientCreated(psychologistId: string, patientId: string) {
        await this.logEvent('patient_created', {
            psychologist_id: psychologistId,
            patient_id: patientId,
        });
    }

    async logRiskAlertSent(psychologistId: string, patientId: string, severity: string) {
        await this.logEvent('risk_alert_sent', {
            psychologist_id: psychologistId,
            patient_id: patientId,
            severity: severity,
        });
    }

    async logDiaryEntryCreated(patientId: string, hasAiAnalysis: boolean) {
        await this.logEvent('diary_entry_created', {
            patient_id: patientId,
            has_ai_analysis: hasAiAnalysis,
        });
    }

    async logReportGenerated(psychologistId: string, patientId: string, reportType: string) {
        await this.logEvent('report_generated', {
            psychologist_id: psychologistId,
            patient_id: patientId,
            report_type: reportType,
        });
    }

    async logAppointmentScheduled(psychologistId: string, patientId: string) {
        await this.logEvent('appointment_scheduled', {
            psychologist_id: psychologistId,
            patient_id: patientId,
        });
    }

    async logSupportTicketCreated(userId: string, ticketType: string) {
        await this.logEvent('support_ticket_created', {
            user_id: userId,
            ticket_type: ticketType,
        });
    }

    /**
     * Eventos de Conversão - Planos
     */
    async logPlanUpgrade(psychologistId: string, fromPlan: string, toPlan: string) {
        await this.logEvent('plan_upgrade', {
            psychologist_id: psychologistId,
            from_plan: fromPlan,
            to_plan: toPlan,
            value: this.getPlanValue(toPlan),
            currency: 'BRL',
        });
    }

    async logTrialStarted(psychologistId: string) {
        await this.logEvent('trial_started', {
            psychologist_id: psychologistId,
            trial_duration_days: 7,
        });
    }

    /**
     * Método genérico para logar eventos
     */
    private async logEvent(eventName: string, params: Record<string, any> = {}) {
        if (!this.isEnabled) return;

        try {
            await FirebaseAnalytics.logEvent({
                name: eventName,
                params: params,
            });
            logger.log(`[Analytics] Evento: ${eventName}`, params);
        } catch (error) {
            logger.error(`[Analytics] Erro ao logar evento ${eventName}:`, error);
        }
    }

    /**
     * Helper para obter valor monetário do plano
     */
    private getPlanValue(planName: string): number {
        const planValues: Record<string, number> = {
            'Gratuito': 0,
            'Básico': 49.90,
            'Pro': 99.90,
            'Clínico': 199.90,
        };
        return planValues[planName] || 0;
    }

    /**
     * Configurar propriedades de usuário
     */
    async setUserProperties(userId: string, userType: string, plan?: string) {
        if (!this.isEnabled) return;

        try {
            await FirebaseAnalytics.setUserId({ userId });
            await FirebaseAnalytics.setUserProperty({
                name: 'user_type',
                value: userType,
            });

            if (plan) {
                await FirebaseAnalytics.setUserProperty({
                    name: 'plan',
                    value: plan,
                });
            }

            logger.log(`[Analytics] Propriedades de usuário configuradas: ${userId}`);
        } catch (error) {
            logger.error('[Analytics] Erro ao configurar propriedades:', error);
        }
    }
}

export default AnalyticsService.getInstance();
